import { useCallback, useEffect, useMemo, useState } from "react";
import {
  acceptProposal,
  formatDate,
  formatPrice,
  listAdminMissionProposals,
  listAdminMissions,
  reviewAdminMission,
  STATUS_LABELS,
  typeLabel,
} from "../../utils/missionsApi";
import "../missions/MissionsLayout.css";

function personName(user, fallback = "—") {
  return [user?.prenom, user?.nom].filter(Boolean).join(" ").trim() || fallback;
}

export default function MissionApplicationsAdminPage() {
  const [pendingMissions, setPendingMissions] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [busyId, setBusyId] = useState("");
  const [pricing, setPricing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const [missionsData, proposalsData] = await Promise.all([
        listAdminMissions({ status: "en_attente_validation" }),
        listAdminMissionProposals({ status: "envoyee,vue" }),
      ]);
      setPendingMissions(missionsData.items || []);
      setProposals(proposalsData.items || []);
    } catch (e) {
      setMessage(e?.response?.data?.error || e?.response?.data?.message || "Erreur lors du chargement admin.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updatePricing = (id, patch) => {
    setPricing((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const approveMission = async (mission) => {
    const values = pricing[mission._id] || {};
    const clientPrice = Number(values.clientPrice || mission.clientPrice || mission.estimatedBudget || 0);
    const providerPrice = Number(values.providerPrice || mission.providerPrice || 0);
    setBusyId(mission._id);
    try {
      await reviewAdminMission(mission._id, {
        action: "approve",
        clientPrice,
        providerPrice,
        adminPricingNote: values.note || "",
      });
      setMessage("Mission validée et publiée.");
      await load();
    } catch (e) {
      setMessage(e?.response?.data?.error || e?.response?.data?.message || "Validation impossible.");
    } finally {
      setBusyId("");
    }
  };

  const refuseMission = async (mission) => {
    const note = window.prompt("Motif du refus ?", pricing[mission._id]?.note || "");
    if (note === null) return;
    setBusyId(mission._id);
    try {
      await reviewAdminMission(mission._id, { action: "refuse", note });
      setMessage("Mission refusée.");
      await load();
    } catch (e) {
      setMessage(e?.response?.data?.error || e?.response?.data?.message || "Refus impossible.");
    } finally {
      setBusyId("");
    }
  };

  const assignProposal = async (proposal) => {
    const id = proposal._id;
    const values = pricing[id] || {};
    const mission = proposal.missionId || {};
    const clientPrice = Number(values.clientPrice || mission.clientPrice || mission.estimatedBudget || proposal.price || 0);
    const providerPrice = Number(values.providerPrice || proposal.price || 0);
    if (!window.confirm(`Attribuer cette mission à ${proposal.providerSnapshot?.name || personName(proposal.providerId, "ce prestataire")} ?`)) return;

    setBusyId(id);
    try {
      await acceptProposal(id, {
        clientPrice,
        providerPrice,
        adminPricingNote: values.note || "",
      });
      setMessage("Mission attribuée. Le prestataire devra confirmer sa présence.");
      await load();
    } catch (e) {
      setMessage(e?.response?.data?.message || "Attribution impossible.");
    } finally {
      setBusyId("");
    }
  };

  const grouped = useMemo(() => {
    return proposals.reduce((acc, proposal) => {
      const key = proposal.missionId?._id || "mission-inconnue";
      if (!acc[key]) acc[key] = { mission: proposal.missionId, proposals: [] };
      acc[key].proposals.push(proposal);
      return acc;
    }, {});
  }, [proposals]);

  return (
    <div className="pmm-shell" style={{ minHeight: "auto" }}>
      <main className="pmm-main" style={{ maxWidth: 1180, margin: "0 auto", width: "100%" }}>
        <div className="pmm-topbar">
          <div>
            <h1 className="pmm-h-accent">Admin missions</h1>
            <div className="pmm-topbar-sub">
              Validez les annonces, ajustez la marge, puis attribuez les candidatures aux prestataires.
            </div>
          </div>
          <button className="pmm-btn" onClick={load} disabled={loading}>
            Actualiser
          </button>
        </div>

        {message && (
          <div className="pmm-card" style={{ padding: 12, background: "var(--cream-warm)" }}>
            {message}
          </div>
        )}

        <section className="pmm-card">
          <h2 style={{ marginTop: 0 }}>Missions à valider ({pendingMissions.length})</h2>
          {loading ? <div className="pmm-spinner" /> : pendingMissions.length === 0 ? (
            <div className="pmm-empty">
              <div className="pmm-empty-icon">✓</div>
              <h3>Aucune mission en attente</h3>
              <p>Les nouvelles missions créées par les pros apparaîtront ici avant publication.</p>
            </div>
          ) : (
            <div className="pmm-grid pmm-grid--2">
              {pendingMissions.map((mission) => {
                const values = pricing[mission._id] || {};
                const clientPrice = values.clientPrice ?? mission.clientPrice ?? mission.estimatedBudget ?? "";
                const providerPrice = values.providerPrice ?? mission.providerPrice ?? "";
                const margin = (Number(clientPrice) || 0) - (Number(providerPrice) || 0);
                return (
                  <article key={mission._id} className="pmm-card" style={{ background: "var(--cream-warm)" }}>
                    <h3 style={{ marginTop: 0 }}>{mission.title}</h3>
                    <p style={{ color: "var(--text-muted)", marginTop: -6 }}>
                      {typeLabel(mission.type)} • {mission.city || "—"} • {formatDate(mission.date)}
                    </p>
                    <p style={{ color: "var(--text-secondary)" }}>{mission.description || "Pas de description."}</p>
                    <div className="pmm-chip pmm-chip--neutral">
                      Client pro : {personName(mission.createdBy, mission.companyName || "—")} — {mission.createdBy?.email || "email manquant"}
                    </div>

                    <div className="pmm-row pmm-row--3" style={{ marginTop: 12 }}>
                      <div className="pmm-field">
                        <label>Prix facturé au pro</label>
                        <input
                          type="number"
                          min={0}
                          value={clientPrice}
                          onChange={(e) => updatePricing(mission._id, { clientPrice: e.target.value })}
                        />
                      </div>
                      <div className="pmm-field">
                        <label>Budget prestataire</label>
                        <input
                          type="number"
                          min={0}
                          value={providerPrice}
                          onChange={(e) => updatePricing(mission._id, { providerPrice: e.target.value })}
                        />
                      </div>
                      <div className="pmm-field">
                        <label>Marge estimée</label>
                        <input value={formatPrice(margin)} readOnly />
                      </div>
                    </div>
                    <div className="pmm-field">
                      <label>Note admin</label>
                      <textarea
                        value={values.note || ""}
                        onChange={(e) => updatePricing(mission._id, { note: e.target.value })}
                        placeholder="Infos call, vérification, marge..."
                      />
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="pmm-btn pmm-btn--primary" disabled={busyId === mission._id} onClick={() => approveMission(mission)}>
                        Valider et publier
                      </button>
                      <button className="pmm-btn pmm-btn--danger" disabled={busyId === mission._id} onClick={() => refuseMission(mission)}>
                        Refuser
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="pmm-card">
          <h2 style={{ marginTop: 0 }}>Candidatures à attribuer ({proposals.length})</h2>
          {loading ? <div className="pmm-spinner" /> : proposals.length === 0 ? (
            <div className="pmm-empty">
              <div className="pmm-empty-icon">📭</div>
              <h3>Aucune candidature en attente</h3>
              <p>Quand un ProMeet Partner postule, sa candidature arrive ici pour attribution.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {Object.entries(grouped).map(([missionId, group]) => (
                <div key={missionId} className="pmm-card" style={{ background: "var(--cream-warm)" }}>
                  <h3 style={{ marginTop: 0 }}>
                    {group.mission?.title || "Mission supprimée"}{" "}
                    <span className="pmm-chip pmm-chip--info">{STATUS_LABELS[group.mission?.status] || group.mission?.status}</span>
                  </h3>
                  <p style={{ color: "var(--text-muted)" }}>
                    {typeLabel(group.mission?.type)} • {group.mission?.city || "—"} • Client : {personName(group.mission?.createdBy, "—")}
                  </p>
                  <div className="pmm-grid pmm-grid--2">
                    {group.proposals.map((proposal) => {
                      const values = pricing[proposal._id] || {};
                      const clientPrice = values.clientPrice ?? group.mission?.clientPrice ?? group.mission?.estimatedBudget ?? "";
                      const providerPrice = values.providerPrice ?? proposal.price ?? "";
                      const margin = (Number(clientPrice) || 0) - (Number(providerPrice) || 0);
                      return (
                        <article key={proposal._id} className="pmm-card">
                          <strong>{proposal.providerSnapshot?.name || personName(proposal.providerId, "Prestataire")}</strong>
                          <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
                            {proposal.providerSnapshot?.city || proposal.providerId?.ville || "—"} • {proposal.providerId?.email || "email manquant"}
                          </div>
                          <div className="pmm-row pmm-row--3" style={{ marginTop: 12 }}>
                            <div className="pmm-field">
                              <label>Prix pro</label>
                              <input
                                type="number"
                                min={0}
                                value={clientPrice}
                                onChange={(e) => updatePricing(proposal._id, { clientPrice: e.target.value })}
                              />
                            </div>
                            <div className="pmm-field">
                              <label>Prix prestataire</label>
                              <input
                                type="number"
                                min={0}
                                value={providerPrice}
                                onChange={(e) => updatePricing(proposal._id, { providerPrice: e.target.value })}
                              />
                            </div>
                            <div className="pmm-field">
                              <label>Marge</label>
                              <input value={formatPrice(margin)} readOnly />
                            </div>
                          </div>
                          <div style={{ display: "grid", gap: 6, margin: "8px 0", color: "var(--text-secondary)", fontSize: 14 }}>
                            <span>Offre prestataire : {formatPrice(proposal.price)} • Délai : {proposal.estimatedDelay || "—"}</span>
                            {proposal.message && <span>{proposal.message}</span>}
                            {proposal.providerSnapshot?.phone && <a href={`tel:${proposal.providerSnapshot.phone}`}>Appeler {proposal.providerSnapshot.phone}</a>}
                          </div>
                          <button className="pmm-btn pmm-btn--primary" disabled={busyId === proposal._id} onClick={() => assignProposal(proposal)}>
                            Attribuer à ce prestataire
                          </button>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
